import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TextField, Box } from "@mui/material";
import React, { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { ComponentModal, IProduct, IGrillingConfig } from "../common/interfaces";
import { usePosStore } from "../context/posStore";
import { useModalStore } from "../context/modalStore";
import { productsService } from "../services/productsService";
import { CommonService } from "../services/CommonService";

// Cantidad mínima permitida (50 gramos = 0.05 kg)
const MIN_QTY = 0.05;

export const CartView: React.FC = () => {
  const messagesEndRef = React.useRef<HTMLTableElement>(null);
  const products = usePosStore((state) => state.products)
  const setProducts = usePosStore((state) => state.setProducts)
  const modalManager = useModalStore()

  useEffect(() => {
    scrollToBottom();
  }, [products])

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView(false)

  const deleteItem = (row: IProduct): void => {
    modalManager.openModal(ComponentModal.CancelItem, {item: row})
  };

  const toggleGrillingService = async (row: IProduct): Promise<void> => {
    const currentlyActive = row.grilling_config?.has_grilling_service || false;
    const quantity = Number(row.quantity);

    // Toggle: si está activo, desactivar; si está inactivo, activar
    const newConfig: IGrillingConfig = {
      has_grilling_service: !currentlyActive,
      has_complements: false,
      complements_quantity: 0
    };

    try {
      if (newConfig.has_grilling_service) {
        // ACTIVAR: Obtener info del servicio y agregar línea al carrito
        const serviceInfo = await productsService.getGrillingServiceInfo(row.id, quantity);

        if (serviceInfo.data?.grilling_service) {
          const grillingService = serviceInfo.data.grilling_service;

          // Crear producto de servicio de asado con propiedades mínimas requeridas
          const grillingProduct = {
            id: grillingService.id,
            name: grillingService.name,
            price_1: grillingService.price_per_kg,
            quantity: quantity,
            is_grilling_service: true,
            parent_product_id: row.id,
            force_kitchen: true,
            // Propiedades requeridas por IProduct con valores por defecto
            description: '',
            image: null,
            type: 'service',
            min_stock: '0',
            max_stock: '0',
            product_loss_percentage: '0',
            department_id: 0,
            product_type_id: 0,
            cost: '0',
            price_type: 0,
            price_2: 0,
            price_3: 0,
            price_4: 0,
            price_5: 0,
            price_iva: 0,
            default_price: null,
            book_id: null,
            iva_id: null,
            ieps_id: null,
            unit_id: null,
            supplier_id: 0,
            barcode: '',
            alt_code: '',
            fiscal_unit_id: '',
            fiscal_prod_serv_id: '',
            hidden: '0',
            is_active: null,
            is_bulk: '0',
            created_by: '',
            edited_by: null,
            created_at: new Date(),
            updated_at: new Date(),
            deleted_at: null,
            inventory: []
          } as IProduct;

          // Actualizar producto original con la configuración e insertar servicio justo después
          const updatedProducts: IProduct[] = [];
          products.forEach((p: IProduct) => {
            if (p.id === row.id && p === row) {
              // Agregar producto padre actualizado
              updatedProducts.push({
                ...p,
                grilling_config: newConfig
              });
              // Agregar servicio de asado inmediatamente después
              updatedProducts.push(grillingProduct);
            } else {
              updatedProducts.push(p);
            }
          });

          setProducts(updatedProducts);

          CommonService.toast('Servicio de asado activado', 'success');
        }
      } else {
        // DESACTIVAR: Remover línea de servicio de asado y actualizar config
        const updatedProducts = products
          .filter((p: IProduct) => {
            // Remover el servicio de asado asociado a este producto
            if (p.is_grilling_service && p.parent_product_id === row.id) {
              return false;
            }
            return true;
          })
          .map((p: IProduct) => {
            // Actualizar configuración del producto original
            if (p.id === row.id && p === row) {
              return {
                ...p,
                grilling_config: newConfig
              };
            }
            return p;
          });

        setProducts(updatedProducts);
        CommonService.toast('Servicio de asado desactivado', 'success');
      }

    } catch (error) {
      console.error('Error toggling grilling service:', error);
      CommonService.toast('Error al actualizar servicio de asado', 'error');
    }
  };

  return (
    <TableContainer component={Paper} style={{ maxHeight: "600px" }}>
      <Table
        sx={{ minWidth: "80%" }}
        aria-label="simple table"
        style={{ backgroundColor: "#333", color: "#fff" }}
        ref={messagesEndRef}
      >
        <TableHead>
          <TableRow>
            <TableCell width={"10%"}></TableCell>
            <TableCell align="left" width={"20%"}>
              Cantidad
            </TableCell>
            <TableCell width={"30%"}>Producto</TableCell>
            <TableCell width={"10%"}>Precio</TableCell>
            <TableCell align="right" width={"10%"}>
              IVA
            </TableCell>
            <TableCell align="right" width={"10%"}>
              IEPS
            </TableCell>
            <TableCell align="right" width={"10%"}>
              TOTAL
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((row) => {
            const iva = row.iva?.porcentaje ?? 0;
            const ieps = row.ieps?.porcentaje ?? 0;
            const quantity = Number(row.quantity);

            // Si la cantidad es inválida (vacía, 0, NaN), no calcular totales
            const isValidQuantity = !isNaN(quantity) && quantity > 0;

            // Determinar si tiene servicio de asado activo
            const hasGrillingService = row.grilling_config?.has_grilling_service || false;
            const isCarnesProduct = productsService.isCarnesFamily(row);
            const isGrillingServiceRow = row.is_grilling_service || false;

            // Usar precio base sin modificar
            const price = Number(row.price_1) || 0;

            const lineIva = isValidQuantity ? price * iva * quantity : 0;
            const lineIeps = isValidQuantity ? price * ieps * quantity : 0;
            const subTotal = isValidQuantity ? price * quantity : 0;
            const total = subTotal + lineIva + lineIeps;

            return (
              <TableRow
                key={row.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  backgroundColor: hasGrillingService
                    ? "#ff6f00"
                    : isGrillingServiceRow
                    ? "rgba(255, 111, 0, 0.15)"
                    : "inherit",
                  borderLeft: isGrillingServiceRow ? "4px solid #ff6f00" : "none",
                  borderTop: isGrillingServiceRow ? "none" : undefined,
                  "&:hover": {
                    backgroundColor: hasGrillingService
                      ? "#ff8f00"
                      : isGrillingServiceRow
                      ? "rgba(255, 111, 0, 0.25)"
                      : "inherit",
                  }
                }}
              >
                <TableCell
                  align="left"
                  component="th"
                  scope="row"
                  sx={{
                    paddingLeft: isGrillingServiceRow ? '32px' : undefined,
                  }}
                >
                  <Box display="flex" gap={1}>
                    {!row.is_grilling_service && (
                      <>
                        <IconButton
                          aria-label="delete"
                          onClick={() => deleteItem(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {isCarnesProduct && (
                          <IconButton
                            aria-label="grilling-service"
                            onClick={() => toggleGrillingService(row)}
                            sx={{
                              fontSize: "1.5rem",
                              color: hasGrillingService ? "#fff" : "#ff6f00",
                              "&:hover": {
                                backgroundColor: "rgba(255, 111, 0, 0.1)",
                              }
                            }}
                          >
                            🔥
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" component="th" scope="row">
                  <TextField
                    type="number"
                    disabled={row.is_grilling_service || false}
                    // inputProps={{
                    //   inputMode: "numeric",
                    // }}
                    value={row.quantity}
                    onChange={(event) => {
                      let value = event.target.value;
                      const newProducts = products.map(
                        (product: IProduct) => {
                          if (product.id === row.id) {
                            const numValue = parseFloat(value);

                            // Permitir valores temporales mientras se escribe
                            if (value === '' || value === '0' || value === '0.' || value.startsWith('0.0')) {
                              // Permitir temporalmente para poder escribir 0.5, 0.05, etc.
                              product.quantity = value;
                            } else if (numValue < 0) {
                              // Rechazar valores negativos inmediatamente
                              product.quantity = 1;
                            } else {
                              // Aceptar el valor mientras se escribe
                              if (product.is_bulk === "1") {
                                product.quantity = parseInt(value);
                              } else {
                                product.quantity = value;
                              }
                            }
                          }
                          // Actualizar cantidad del servicio de asado si existe
                          if (product.is_grilling_service && product.parent_product_id === row.id) {
                            product.quantity = value;
                          }
                          return product;
                        }
                      );
                      setProducts(newProducts);
                    }}
                    onBlur={(event) => {
                      let value = event.target.value;

                      // Primero calcular la cantidad final del producto padre
                      let finalQuantity: number | string = 1;
                      const numValue = parseFloat(value);

                      if (
                        isNaN(numValue) ||
                        value === '' ||
                        value === '0.' ||
                        numValue < MIN_QTY
                      ) {
                        finalQuantity = 1;
                      } else {
                        if (row.is_bulk === "1") {
                          finalQuantity = parseInt(value);
                        } else {
                          finalQuantity = parseFloat(value);
                        }
                      }

                      // Ahora actualizar todos los productos
                      const newProducts = products.map(
                        (product: IProduct) => {
                          if (product.id === row.id) {
                            product.quantity = finalQuantity;
                          }
                          // Actualizar cantidad del servicio de asado si existe
                          if (product.is_grilling_service && product.parent_product_id === row.id) {
                            product.quantity = finalQuantity;
                          }
                          return product;
                        }
                      );
                      setProducts(newProducts);
                    }}
                  />
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    paddingLeft: isGrillingServiceRow ? '32px' : undefined,
                  }}
                >
                  {isGrillingServiceRow && (
                    <span style={{ marginRight: '8px', color: '#ff6f00', fontWeight: 'bold' }}>
                      ↳ 🔥
                    </span>
                  )}
                  {row.name} <br />
                  {row.selectedOptions
                    ? row.selectedOptions.split(",").map((e: string) => {
                        return (
                          <div key={e}>
                            - {e} <br />
                          </div>
                        );
                      })
                    : ""}{" "}
                  {/* {row.assembled_products
                    ? row.assembled_products.map((product: IProduct) => {
                        return (
                          <div key={product.id}>
                            * {product.name} <br />
                          </div>
                        );
                      })
                    : ""} */}
                </TableCell>
                <TableCell align="right">{price.toFixed(2)}</TableCell>
                <TableCell align="right">{isValidQuantity ? lineIva.toFixed(2) : '-'}</TableCell>
                <TableCell align="right">{isValidQuantity ? lineIeps.toFixed(2) : '-'}</TableCell>
                <TableCell align="right">{isValidQuantity ? total.toFixed(2) : '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
